import { useEffect, useRef } from 'react';
import useStore from '../../store/useStore';

/**
 * Invisible sentinel element placed at the desired scroll depth to trigger
 * the dark mode transition.  When it enters the viewport the store's
 * `isDark` flips to true; when it leaves upward, it flips back.
 *
 * This is a secondary mechanism — the primary one is scroll-progress
 * threshold in useScrollEngine. Having both keeps transitions crisp when
 * the user snaps to a specific position.
 */
export default function DarkModeMarker() {
    const ref = useRef<HTMLDivElement>(null);
    const { setIsDark } = useStore.getState().methods;

    // Expose the threshold so the scroll engine and this component agree
    const threshold = useStore((s) => s.darkModeThreshold);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsDark(entry.isIntersecting);
            },
            { threshold: 0 },
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [setIsDark]);

    // Position the marker at `threshold` of total page height
    // The LandingPage wrapper sets --page-height via a ResizeObserver
    return (
        <div
            ref={ref}
            aria-hidden="true"
            style={{ position: 'absolute', top: `${threshold * 100}%`, height: 1, width: '100%' }}
        />
    );
}


